import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Always format in Thai time: the server renders in UTC while browsers are in
// Asia/Bangkok, so local-time formatting gave a different day after 17:00 UTC
// (hydration mismatch, e.g. "30 ก.ย." on the server vs "1 ต.ค." in the browser).
const THAI_DATE = new Intl.DateTimeFormat("th-TH-u-ca-gregory", {
  timeZone: "Asia/Bangkok",
  day: "numeric",
  month: "short",
  year: "numeric",
})

export function formatThaiDate(date: Date): string {
  const parts = Object.fromEntries(THAI_DATE.formatToParts(date).map((p) => [p.type, p.value]))
  return `${parts.day} ${parts.month} ${Number(parts.year) + 543}`
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    minimumFractionDigits: 0,
  }).format(amount)
}

/**
 * Compact currency: 37,750,000 → "37.75M" · 850,000 → "850K" · 1.2B → "1.20B"
 * Returns just the number + suffix (no ฿). Wrap with a "฿" prefix at the call site
 * if you want the symbol.
 */
export function formatCompactCurrency(amount: number, fractionDigits = 2): string {
  if (amount === 0) return "0"
  const abs = Math.abs(amount)
  const sign = amount < 0 ? "-" : ""
  if (abs >= 1_000_000_000) {
    return `${sign}${(abs / 1_000_000_000).toFixed(fractionDigits)}B`
  }
  if (abs >= 1_000_000) {
    return `${sign}${(abs / 1_000_000).toFixed(fractionDigits)}M`
  }
  if (abs >= 1_000) {
    return `${sign}${(abs / 1_000).toFixed(1)}K`
  }
  return `${sign}${abs.toLocaleString()}`
}

export function generateRQNumber(
  category: "P" | "S" | "T",
  running: number,
  date: Date = new Date(),
): string {
  const buddhistYear = (date.getFullYear() + 543) % 100
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const seq = String(running).padStart(2, "0")
  return `RQ-${buddhistYear}-${month}-RQ${seq}${category}`
}
